import { axiosInstance } from "@/lib/axios";
import { ZodError } from "zod";
import { loginRequestSchema, loginResponseSchema, profileResponseSchema, type LoginRequest, type LoginResponse, type ProfileResponse } from "../types/schemas";
import { API_ENDPOINTS, HTTP_STATUS } from "@/lib/api-constants";

export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  try {
    const sanitizedCredentials = {
      ...credentials,
      recaptcha_response: credentials.recaptcha_response || ""
    };

    const validatedData = loginRequestSchema.parse(sanitizedCredentials);

    const response = await axiosInstance.post(API_ENDPOINTS.AUTH.LOGIN, validatedData);

    const validatedResponse = loginResponseSchema.parse(response.data);

    return validatedResponse;
  } catch (error) {
    if (error instanceof ZodError) {
      const errorMessages = error.issues.map((issue) => issue.message).join(', ');
      throw new Error(`Invalid request data: ${errorMessages}`);
    }

    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { message?: string };
        };
        message?: string;
      };
      const status = axiosError.response?.status;
      const message = axiosError.response?.data?.message || axiosError.message || 'Unknown error';

      switch (status) {
        case HTTP_STATUS.UNAUTHORIZED:
          throw new Error('Invalid email or password');
        case HTTP_STATUS.TOO_MANY_REQUESTS:
          throw new Error('Too many login attempts. Please try again later.');
        case HTTP_STATUS.BAD_REQUEST:
          throw new Error(`Invalid request: ${message}`);
        default:
          throw new Error(message || 'Login failed. Please try again.');
      }
    }

    throw new Error('Network error. Please check your connection and try again.');
  }
};

export const getProfile = async (): Promise<ProfileResponse> => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.AUTH.PROFILE);
    const validatedResponse = profileResponseSchema.parse(response.data);
    return validatedResponse;
  } catch (error) {
    if (error instanceof ZodError) {
      console.error('Profile validation error:', error);
      throw new Error('Invalid profile data received');
    }
    throw error;
  }
};

export const switchCompany = async (companyId: string | number): Promise<ProfileResponse> => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.AUTH.SWITCH(companyId));
    const validatedResponse = profileResponseSchema.parse(response.data);
    return validatedResponse;
  } catch (error) {
    if (error instanceof ZodError) {
      console.error('Switch company validation error:', error);
      throw new Error('Invalid response data received');
    }
    throw error;
  }
};
